import React, { useRef } from 'react';
import { ui, btnGhost } from './canvasUi';
import { continueListOnEnter, toggleBullets } from './markdownList';

/**
 * The text block's body editor: a markdown textarea that behaves like a list
 * editor, because dashboard notes are mostly bullets.
 *
 * - A "Bullets" button toggles `- ` across the selected lines.
 * - Enter inside a list continues it; Enter on an empty item ends it.
 *
 * The markdown renderer already understood `-` lists; this is purely about not
 * having to type the syntax on every line. Shared by the config drawer and the
 * on-card editor so both behave identically.
 */

const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minHeight: 0 },
    bar: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
    btn: { ...btnGhost, padding: '4px 9px', fontSize: '11.5px' },
    hint: { fontSize: '11px', color: ui.ink2, marginLeft: 'auto' },
};

export default function TextBlockArea({ value, onChange, style, placeholder, autoFocus, testId }) {
    const ref = useRef(null);

    /** Apply a pure transform, then restore the caret React would otherwise drop to the end. */
    const apply = (next, selStart, selEnd) => {
        onChange(next);
        requestAnimationFrame(() => {
            const el = ref.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(selStart, selEnd ?? selStart);
        });
    };

    const onKeyDown = (e) => {
        if (e.key !== 'Enter' || e.shiftKey) return;
        const el = e.target;
        // Only take over a plain caret — with a selection, the default
        // replace-selection behaviour is what the user means.
        if (el.selectionStart !== el.selectionEnd) return;
        const next = continueListOnEnter(el.value, el.selectionStart);
        if (!next) return;
        e.preventDefault();
        apply(next.value, next.caret);
    };

    const onBullets = () => {
        const el = ref.current;
        if (!el) return;
        const next = toggleBullets(el.value, el.selectionStart, el.selectionEnd);
        apply(next.value, next.selectionStart, next.selectionEnd);
    };

    return (
        <div style={s.wrap}>
            <div style={s.bar}>
                <button type="button" className="cvx-ghost cvx-focus" style={s.btn}
                    onClick={onBullets} data-testid="canvas-text-bullets"
                    title="Turn the selected lines into a bullet list">• Bullets</button>
                <span style={s.hint}>Enter continues a list</span>
            </div>
            <textarea ref={ref} className="cvx-focus" style={style} value={value} autoFocus={autoFocus}
                placeholder={placeholder} data-testid={testId}
                onKeyDown={onKeyDown} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}
